import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "macroeconomics-formulas-models-quick-reference",
  category: "subject-explainers",
  authorSlug: "uday-kumar",
  title: "Macroeconomics Formula Sheet & Models: IS-LM, Solow, Phillips Curve & Policy Multipliers",
  description: "Your comprehensive guide to Macroeconomics I & II. Covers National Income, IS-LM, AD-AS, Solow Growth Model, and more.",
  pubDate: "2026-08-20",
  tags: ["macroeconomics", "formulas", "is-lm", "solow"],
  readMinutes: 13,
  heroEyebrow: "Subject Explainer · Macro Formulas",
};

export function Body() {
  return (
    <article className="max-w-4xl mx-auto space-y-8">
      <header className="mb-10">
        <h1 className="text-4xl font-bold mb-4 text-primary">Macroeconomics Formula Sheet &amp; Models</h1>
        <p className="text-xl text-muted-foreground">IS-LM, Solow, Phillips Curve &amp; Policy Multipliers</p>
      </header>

      <section>
        <h2 className="text-2xl font-bold mb-4 border-b border-rule pb-2">1. National Income Accounting &amp; Key Multipliers</h2>
        <ul className="list-disc pl-6 space-y-3 text-base">
          <li><strong>GDP Identity (Expenditure Method):</strong> <span className="font-mono text-grade">Y = C + I + G + NX</span>
            <p className="text-sm text-muted-foreground mt-1">The fundamental accounting identity where Y is output, C is consumption, I is investment, G is government spending, and NX is net exports.</p>
          </li>
          <li><strong>Consumption Function:</strong> <span className="font-mono text-grade">C = C0 + c(Y - T)</span>
            <p className="text-sm text-muted-foreground mt-1">C0 is autonomous consumption, c is marginal propensity to consume (MPC), Y-T is disposable income.</p>
          </li>
          <li><strong>Keynesian Government Spending Multiplier:</strong> <span className="font-mono text-grade">dY/dG = 1 / (1 - c)</span>
            <p className="text-sm text-muted-foreground mt-1">Assuming closed economy and lump-sum taxes. Exam tip: Multiplier decreases if taxes are proportional to income (t).</p>
          </li>
          <li><strong>Tax Multiplier:</strong> <span className="font-mono text-grade">dY/dT = -c / (1 - c)</span>
            <p className="text-sm text-muted-foreground mt-1">Note it is negative and strictly less than the spending multiplier in magnitude.</p>
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4 border-b border-rule pb-2">2. The IS-LM Model</h2>
        <p className="mb-4">Equilibrium in the goods market (IS) and money market (LM).</p>
        <ul className="list-disc pl-6 space-y-3">
          <li><strong>IS Curve Equation:</strong> <span className="font-mono text-grade">Y = C(Y-T) + I(r) + G</span>
            <p className="text-sm text-muted-foreground mt-1">Shows combinations of interest rate (r) and output (Y) where the goods market clears. Downward sloping because higher r reduces investment (I).</p>
          </li>
          <li><strong>LM Curve Equation:</strong> <span className="font-mono text-grade">M/P = L(r, Y)</span>
            <p className="text-sm text-muted-foreground mt-1">Shows combinations of r and Y where money supply (M/P) equals money demand (L). Upward sloping because higher Y increases transaction demand for money, raising r.</p>
          </li>
          <li><strong>Mundell-Fleming Model (Open Economy IS-LM):</strong>
            <p className="text-sm text-muted-foreground mt-1">Incorporates the exchange rate (e) and net exports NX(e). Under perfect capital mobility (r = r*), Fiscal Policy is highly effective under fixed exchange rates, but completely ineffective under floating rates. Monetary policy is the exact opposite.</p>
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4 border-b border-rule pb-2">3. Solow Growth Model</h2>
        <p className="mb-4">Understanding long-run economic growth through capital accumulation.</p>
        <ul className="list-disc pl-6 space-y-3">
          <li><strong>Production Function (Intensive form):</strong> <span className="font-mono text-grade">y = f(k)</span>
            <p className="text-sm text-muted-foreground mt-1">Where y is output per worker (Y/L) and k is capital per worker (K/L). Often assumed Cobb-Douglas: y = k^alpha.</p>
          </li>
          <li><strong>Capital Accumulation Equation:</strong> <span className="font-mono text-grade">dk/dt = s*f(k) - (delta + n + g)*k</span>
            <p className="text-sm text-muted-foreground mt-1">s is savings rate, delta is depreciation, n is population growth, g is technological progress rate.</p>
          </li>
          <li><strong>Steady State Condition:</strong> <span className="font-mono text-grade">s*f(k*) = (delta + n + g)*k*</span>
            <p className="text-sm text-muted-foreground mt-1">At steady state, capital per worker k is constant (dk/dt = 0).</p>
          </li>
          <li><strong>Golden Rule of Capital:</strong> <span className="font-mono text-grade">MPK = delta + n + g</span>
            <p className="text-sm text-muted-foreground mt-1">The steady state that maximizes consumption per worker. MPK is the marginal product of capital.</p>
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4 border-b border-rule pb-2">4. Inflation, Unemployment &amp; AD-AS</h2>
        <ul className="list-disc pl-6 space-y-3">
          <li><strong>Expectations-Augmented Phillips Curve:</strong> <span className="font-mono text-grade">pi = pi_e - beta(u - u_n) + v</span>
            <p className="text-sm text-muted-foreground mt-1">pi is actual inflation, pi_e is expected inflation, u is unemployment, u_n is natural rate of unemployment, v is supply shocks.</p>
          </li>
          <li><strong>Okun's Law:</strong> <span className="font-mono text-grade">(Y - Y_bar) / Y_bar = -alpha * (u - u_n)</span>
            <p className="text-sm text-muted-foreground mt-1">The negative relationship between the output gap and cyclical unemployment.</p>
          </li>
          <li><strong>Money Supply Multiplier:</strong> <span className="font-mono text-grade">m = (cr + 1) / (cr + rr)</span>
            <p className="text-sm text-muted-foreground mt-1">M = m * B. cr is currency-deposit ratio, rr is reserve-deposit ratio, B is monetary base.</p>
          </li>
          <li><strong>Taylor Rule:</strong> <span className="font-mono text-grade">i = r_n + pi + a_pi*(pi - pi*) + a_y*(Y - Y_bar)</span>
            <p className="text-sm text-muted-foreground mt-1">A guideline for central banks to set the nominal interest rate based on inflation gaps and output gaps.</p>
          </li>
        </ul>
      </section>
      
    </article>
  );
}


